import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { apiService } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Payroll = () => {
  const { state } = useAuth();
  const token = state.token;
//   const userId = state.user?.id;

//   const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [isPayrollProcessing, setIsPayrollProcessing] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);

  // Initialize payroll summary with zeros to avoid undefined errors.
  const [payrollSummary, setPayrollSummary] = useState({
    totalGrossPay: 0,
    totalNetPay: 0,
    totalDeductions: 0,
    totalEmployees: 0,
    processedEmployees: 0,
    pendingEmployees: 0,
    lastProcessed: ""
  });
  const [departmentsPayroll, setDepartmentsPayroll] = useState([]);
  const [employeesPayroll, setEmployeesPayroll] = useState([]);
  // Separate state for staff records (which include department info)
  const [staffRecords, setStaffRecords] = useState([]);
  const [payrollHistory] = useState([]);

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.4,
        ease: "easeOut",
      },
    }),
  };

  useEffect(() => {
    if (!token) return;
    const fetchPayrollDetails = async () => {
      try {
        const response = await apiService.get("/api/payroll/staffs-payroll", token);
        console.log("Payroll details response:", response.data);
        setEmployeesPayroll(
          Array.isArray(response.data.data) ? response.data.data : []
        );
      } catch (error) {
        console.error("Error fetching payroll details:", error);
      }
    };
    fetchPayrollDetails();
  }, [token]);

  useEffect(() => {
    if (!token) return;
    const fetchStaffRecords = async () => {
      try {
        const response = await apiService.get("/api/staff-management/records", token);
        console.log("Staff records:", response.data);
        const records = response.data?.data;
        setStaffRecords(Array.isArray(records) ? records : []);
      } catch (error) {
        console.error("Error fetching staff records:", error);
      }
    };
    fetchStaffRecords();
  }, [token]);

    // Process staff and payroll data to calculate department summaries
    useEffect(() => {
        if (staffRecords.length > 0 && employeesPayroll.length > 0) {
          // Deduplicate payroll records by staff_id
          const uniquePayroll = [
            ...new Map(employeesPayroll.map(item => [item.staff_id, item])).values()
          ];
      
          // Create a map of staff_id to department name from staff records
          const staffDepartmentMap = staffRecords.reduce((map, staff) => {
            map[staff.staff_id] = staff.department;
            return map;
          }, {});
      
          // Group unique payroll records by department
          const departmentGroups = {};
          uniquePayroll.forEach(payroll => {
            const department = staffDepartmentMap[payroll.staff_id] || 'Unknown';
            if (!departmentGroups[department]) {
              departmentGroups[department] = {
                name: department,
                employees: 0,
                grossAmount: 0,
                netAmount: 0,
                status: 'pending', // default status
                id: department.toLowerCase().replace(/\s+/g, '-')
              };
            }
            departmentGroups[department].employees++; // count only unique employee
            departmentGroups[department].grossAmount += payroll.grossSalary || 0;
            departmentGroups[department].netAmount += payroll.netSalary || 0;
            if (payroll.paymentStatus === "Processed") {
              departmentGroups[department].status = "processed";
            }
          });
      
          const departmentSummaries = Object.values(departmentGroups);
          setDepartmentsPayroll(departmentSummaries);
      
          // Update overall payroll summary using the unique payroll list
          const summary = {
            totalGrossPay: departmentSummaries.reduce((sum, dept) => sum + dept.grossAmount, 0),
            totalNetPay: departmentSummaries.reduce((sum, dept) => sum + dept.netAmount, 0),
            totalDeductions: departmentSummaries.reduce(
              (sum, dept) => sum + (dept.grossAmount - dept.netAmount),
              0
            ),
            totalEmployees: staffRecords.length,
            processedEmployees: uniquePayroll.filter(p => p.paymentStatus === "Processed").length,
            pendingEmployees: uniquePayroll.filter(p => p.paymentStatus !== "Processed").length,
            lastProcessed:
              uniquePayroll.length > 0
                ? new Date(Math.max(...uniquePayroll.map(p => new Date(p.paymentDate)))).toLocaleDateString()
                : ""
          };
          setPayrollSummary(summary);
        }
    }, [staffRecords, employeesPayroll]);
            
    const uniqueEmployees = Array.isArray(employeesPayroll)
  ? [...new Map(employeesPayroll.map(item => [item.staff_id, item])).values()]
  : [];
  

  const handleProcessPayroll = async () => {
    setIsPayrollProcessing(true);
    const monthString = `${selectedYear}-${(selectedMonth + 1)
      .toString()
      .padStart(2, "0")}`;
    try {
      const response = await apiService.post(
        "/api/payroll/process",
        token,
        { month: monthString }
      );
      console.log("Payroll processed:", response.data);
      toast.success("Payroll processed successfully!");
      // Re-fetch updated payroll details
      const payrollResponse = await apiService.get("/api/payroll/staffs-payroll", token);
      console.log("Updated payroll details:", payrollResponse.data);
      setEmployeesPayroll(
        Array.isArray(payrollResponse.data.data)
          ? payrollResponse.data.data
          : []
      );
    } catch (error) {
      console.error("Error processing payroll:", error);
      toast.error("Failed to process payroll.");
    } finally {
      setIsPayrollProcessing(false);
    }
  };

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  return (
    <div className="flex min-h-screen bg-gray-50 font-source-sans-3">
      <div className="flex-1 transition-all duration-300">
        <main className="px-4 py-8">
          {/* Header Section */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-2xl font-bold text-primary-800 source-sans-3">
                  Payroll Management
                </h2>
                <p className="text-primary-600 mt-1 source-sans-3">
                  Manage and process payroll for your employees
                </p>
              </div>
              <div className="mt-4 md:mt-0 flex space-x-3">
                <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                    className="py-2 px-3 border-r border-gray-300 source-sans-3 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  >
                    {months.map((month, index) => (
                      <option key={month} value={index}>
                        {month}
                      </option>
                    ))}
                  </select>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    className="py-2 px-3 source-sans-3 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  >
                    {[2025, 2024, 2023].map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleProcessPayroll}
                  disabled={isPayrollProcessing || staffRecords.length === 0}
                  className={`bg-primary-600 text-white px-4 source-sans-3 py-2 rounded-lg shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center ${
                    isPayrollProcessing ? "animate-pulse" : ""
                  }`}
                >
                  {isPayrollProcessing ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    "Process Payroll"
                  )}
                </motion.button>
              </div>
            </div>
          </motion.div>

          {/* Tabs Navigation */}
          <div className="mb-6 border-b border-gray-200">
            <nav className="flex space-x-8">
              {[
                { id: "overview", label: "Overview" },
                { id: "employees", label: "Employees" },
                { id: "departments", label: "Departments" },
                { id: "history", label: "History" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-3 font-medium text-sm px-1 source-sans-3 border-b-2 ${
                    activeTab === tab.id
                      ? "border-primary-600 text-primary-800"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Overview Tab */}
          {activeTab === "overview" && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {[
                  {
                    title: "Total Gross Pay",
                    value: `$${payrollSummary.totalGrossPay.toLocaleString()}`,
                    icon: (
                      <svg
                        className="w-6 h-6 text-primary-100"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    ),
                  },
                  {
                    title: "Total Net Pay",
                    value: `$${payrollSummary.totalNetPay.toLocaleString()}`,
                    icon: (
                      <svg
                        className="w-6 h-6 text-emerald-100"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                        />
                      </svg>
                    ),
                  },
                  {
                    title: "Total Deductions",
                    value: `$${payrollSummary.totalDeductions.toLocaleString()}`,
                    icon: (
                      <svg
                        className="w-6 h-6 text-amber-100"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    ),
                  },
                  {
                    title: "Employees",
                    value: `${payrollSummary.processedEmployees}/${payrollSummary.totalEmployees}`,
                    icon: (
                      <svg
                        className="w-6 h-6 text-rose-100"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                        />
                      </svg>
                    ),
                  },
                ].map((stat, i) => (
                  <motion.div
                    key={stat.title}
                    custom={i}
                    variants={cardVariants}
                    initial="hidden"
                    animate="visible"
                    className="bg-white rounded-xl shadow-sm overflow-hidden"
                  >
                    <div className="p-5">
                      <div className="flex items-center">
                        <div
                          className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                            i === 0
                              ? "bg-primary-600"
                              : i === 1
                              ? "bg-emerald-600"
                              : i === 2
                              ? "bg-amber-600"
                              : "bg-rose-600"
                          }`}
                        >
                          {stat.icon}
                        </div>
                        <div className="ml-4">
                          <p className="text-sm text-gray-500 font-medium source-sans-3">
                            {stat.title}
                          </p>
                          <p className="text-2xl font-bold text-gray-900 source-sans-3">
                            {stat.value}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div
                      className={`h-1 ${
                        i === 0
                          ? "bg-primary-600"
                          : i === 1
                          ? "bg-emerald-600"
                          : i === 2
                          ? "bg-amber-600"
                          : "bg-rose-600"
                      }`}
                    ></div>
                  </motion.div>
                ))}
              </div>

              {/* Department Payroll Summary */}
              <motion.div
                variants={cardVariants}
                custom={0}
                initial="hidden"
                animate="visible"
                className="bg-white rounded-xl shadow-sm overflow-hidden mb-6"
              >
                <div className="p-5 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-lg text-primary-800 source-sans-3">
                      Department Payroll Summary
                    </h3>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="text-primary-600 hover:text-primary-800 text-sm font-medium source-sans-3"
                    >
                      Export PDF
                    </motion.button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider source-sans-3">
                          Department
                        </th>
                        <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider source-sans-3">
                          Employees
                        </th>
                        <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider source-sans-3">
                          Gross Amount
                        </th>
                        <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider source-sans-3">
                          Net Amount
                        </th>
                        <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider source-sans-3">
                          Status
                        </th>
                        <th className="py-3 px-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider source-sans-3">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {departmentsPayroll.length > 0 ? (
                        departmentsPayroll.map((dept) => (
                          <tr key={dept.id} className="hover:bg-gray-50">
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900 source-sans-3">
                                {dept.name}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900 source-sans-3">
                                {dept.employees}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900 source-sans-3">
                                ${dept.grossAmount.toLocaleString()}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900 source-sans-3">
                                ${dept.netAmount.toLocaleString()}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <span
                                className={`px-2 py-1 inline-flex text-xs leading-5 font-medium rounded-full source-sans-3 ${
                                  dept.status === "processed"
                                    ? "bg-green-100 text-green-800"
                                    : "bg-amber-100 text-amber-800"
                                }`}
                              >
                                {dept.status.charAt(0).toUpperCase() +
                                  dept.status.slice(1)}
                              </span>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <button className="text-primary-600 hover:text-primary-800 source-sans-3 mr-3">
                                View
                              </button>
                              <button className="text-gray-500 hover:text-gray-700 source-sans-3">
                                Export
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="px-4 py-6 text-center text-gray-500">
                            No department payroll data available. Process payroll to see department summaries.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </motion.div>


              {/* Payroll Processing Status */}
              <motion.div
                variants={cardVariants}
                custom={1}
                initial="hidden"
                animate="visible"
                className="bg-white rounded-xl shadow-sm overflow-hidden"
              >
                <div className="p-5 border-b border-gray-100">
                  <h3 className="font-bold text-lg text-primary-800 source-sans-3">
                    Payroll Processing Status
                  </h3>
                </div>
                <div className="p-5">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
                    <div>
                      <h4 className="text-lg font-medium text-gray-900 source-sans-3">
                        {months[selectedMonth]} {selectedYear} Payroll
                      </h4>
                      <p className="text-sm text-gray-500 source-sans-3 mt-1">
                        Last processed on: {payrollSummary.lastProcessed}
                      </p>
                    </div>
                    <div className="mt-4 md:mt-0">
                      {payrollSummary.pendingEmployees > 0 ? (
                        <div className="flex items-center">
                          <span className="px-2.5 py-0.5 bg-amber-100 text-amber-800 text-xs rounded-full source-sans-3 mr-2">
                            {payrollSummary.pendingEmployees} Pending
                          </span>
                          <span className="text-gray-500 text-sm source-sans-3">
                            {payrollSummary.processedEmployees} of {payrollSummary.totalEmployees} processed
                          </span>
                        </div>
                      ) : (
                        <span className="px-2.5 py-0.5 bg-green-100 text-green-800 text-xs rounded-full source-sans-3">
                          Completed
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
                    <div
                      className="bg-primary-600 h-2.5 rounded-full"
                      style={{
                        width: `${
                          payrollSummary.totalEmployees > 0
                            ? (payrollSummary.processedEmployees / payrollSummary.totalEmployees) *
                              100
                            : 0
                        }%`,
                      }}
                    ></div>
                  </div>
                  <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600">
                          <svg
                            className="w-5 h-5"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                            />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-xs text-gray-500 source-sans-3">Payslips Generated</p>
                          <p className="text-lg font-semibold text-gray-900 source-sans-3">
                            {payrollSummary.processedEmployees}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-600">
                          <svg
                            className="w-5 h-5"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-xs text-gray-500 source-sans-3">Pending Approvals</p>
                          <p className="text-lg font-semibold text-gray-900 source-sans-3">
                            {payrollSummary.pendingEmployees}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
                          <svg
                            className="w-5 h-5"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                            />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-xs text-gray-500 source-sans-3">Tax Deductions</p>
                          <p className="text-lg font-semibold text-gray-900 source-sans-3">
                            ${payrollSummary.totalDeductions.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </>
          )}

          {/* Employees Tab */}
          {activeTab === "employees" && (
            <motion.div
              variants={cardVariants}
              custom={0}
              initial="hidden"
              animate="visible"
              className="bg-white rounded-xl shadow-sm overflow-hidden"
            >
              <div className="p-5 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-lg text-primary-800 source-sans-3">
                    Employee Payroll Details
                  </h3>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setShowFilterModal(true)}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg source-sans-3 hover:bg-gray-50 flex items-center"
                    >
                      <svg
                        className="w-4 h-4 mr-1"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                        />
                      </svg>
                      Filter
                    </button>
                    <button className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg source-sans-3 hover:bg-gray-50 flex items-center">
                      <svg
                        className="w-4 h-4 mr-1"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                        />
                      </svg>
                      Export
                    </button>
                  </div>
                </div>
              </div>
              <div className="p-5 border-b border-gray-100">
                <div className="flex flex-col md:flex-row md:items-center space-y-3 md:space-y-0 md:space-x-4">
                  <div className="relative flex-grow">
                    <input
                      type="text"
                      placeholder="Search employees..."
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full source-sans-3 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg
                        className="h-5 w-5 text-gray-400"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <select className="px-3 py-2 border border-gray-300 rounded-lg source-sans-3 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500">
                      <option value="">All Departments</option>
                      <option value="Engineering">Engineering</option>
                      <option value="Marketing">Marketing</option>
                      <option value="Operations">Operations</option>
                      <option value="Finance">Finance</option>
                    </select>
                    <select className="px-3 py-2 border border-gray-300 rounded-lg source-sans-3 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500">
                      <option value="">All Status</option>
                      <option value="processed">Processed</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider source-sans-3">
                        Employee
                      </th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider source-sans-3">
                        Department
                      </th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider source-sans-3">
                        Basic Salary
                      </th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider source-sans-3">
                        Allowances
                      </th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider source-sans-3">
                        Deductions
                      </th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider source-sans-3">
                        Net Pay
                      </th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider source-sans-3">
                        Status
                      </th>
                      <th className="py-3 px-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider source-sans-3">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody className="bg-white divide-y divide-gray-200">
  {uniqueEmployees.map((employee) => {
    // Look up staff record using staff_id
    const staff = staffRecords.find((s) => s.staff_id === employee.staff_id);
    const department = staff ? staff.department : "Unknown";
    // Use staff record to get first and last names if available
    const fullName = staff ? `${staff.firstName} ${staff.lastName}` : `${employee.firstName || ""} ${employee.lastName || ""}`.trim();
    const initials = staff
      ? (staff.firstName.charAt(0) + staff.lastName.charAt(0)).toUpperCase()
      : fullName.split(" ").map(n => n.charAt(0)).join("").toUpperCase();
      
    return (
      <tr key={employee.staff_id} className="hover:bg-gray-50">
        <td className="px-4 py-4 whitespace-nowrap">
          <div className="flex items-center">
            <div className="h-10 w-10 flex-shrink-0">
              <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-primary-800 font-medium text-sm source-sans-3">
                  {initials}
                </span>
              </div>
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-900 source-sans-3">
                {fullName}
              </div>
              <div className="text-sm text-gray-500 source-sans-3">
                {employee.position}
              </div>
            </div>
          </div>
        </td>
        <td className="px-4 py-4 whitespace-nowrap">
          <div className="text-sm text-gray-900 source-sans-3">{department}</div>
        </td>
        <td className="px-4 py-4 whitespace-nowrap">
          <div className="text-sm text-gray-900 source-sans-3">
            ${employee.baseSalary ? employee.baseSalary.toLocaleString() : 0}
          </div>
        </td>
        <td className="px-4 py-4 whitespace-nowrap">
          <div className="text-sm text-gray-900 source-sans-3">
            ${employee.allowances ? employee.allowances.reduce((sum, a) => sum + Number(a.amount), 0).toLocaleString() : 0}
          </div>
        </td>
        <td className="px-4 py-4 whitespace-nowrap">
          <div className="text-sm text-gray-900 source-sans-3">
            ${employee.deductions ? employee.deductions.reduce((sum, d) => sum + Number(d.amount), 0).toLocaleString() : 0}
          </div>
        </td>
        <td className="px-4 py-4 whitespace-nowrap">
          <div className="text-sm font-medium text-gray-900 source-sans-3">
            ${employee.netSalary ? employee.netSalary.toLocaleString() : 0}
          </div>
        </td>
        <td className="px-4 py-4 whitespace-nowrap">
          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-medium rounded-full source-sans-3 ${
            employee.paymentStatus === 'Processed' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
          }`}>
            {employee.paymentStatus
              ? employee.paymentStatus.charAt(0).toUpperCase() + employee.paymentStatus.slice(1)
              : "Pending"}
          </span>
        </td>
        <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
          <button className="text-primary-600 hover:text-primary-800 source-sans-3 mr-3">
            View
          </button>
          <button className="text-gray-500 hover:text-gray-700 source-sans-3">
            Edit
          </button>
        </td>
      </tr>
    );
  })}
</tbody>

                </table>
              </div>
              <div className="px-5 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-500 source-sans-3">
                  Showing <span className="font-medium">1</span> to{" "}
                  <span className="font-medium">{employeesPayroll.length}</span> of{" "}
                  <span className="font-medium">{payrollSummary.totalEmployees}</span>{" "}
                  employees
                </div>
                <div className="flex space-x-2">
                  <button className="px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50 source-sans-3 disabled:opacity-50 disabled:cursor-not-allowed" disabled>
                    Previous
                  </button>
                  <button className="px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50 source-sans-3">
                    Next
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Departments Tab */}
          {activeTab === "departments" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <motion.div
                variants={cardVariants}
                custom={0}
                initial="hidden"
                animate="visible"
                className="bg-white rounded-xl shadow-sm overflow-hidden md:col-span-2"
              >
                <div className="p-5 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-lg text-primary-800 source-sans-3">
                      Department Payroll Overview
                    </h3>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="text-primary-600 hover:text-primary-800 text-sm font-medium source-sans-3"
                    >
                      Export PDF
                    </motion.button>
                  </div>
                </div>
                <div className="p-5">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    {departmentsPayroll.map((dept, index) => (
                      <motion.div
                        key={dept.id}
                        custom={index}
                        variants={cardVariants}
                        initial="hidden"
                        animate="visible"
                        className="border border-gray-200 rounded-lg p-4 hover:border-primary-300 transition-colors duration-200"
                      >
                        <div className="flex justify-between items-start mb-3">
                          <h4 className="font-medium text-gray-900 source-sans-3">
                            {dept.name}
                          </h4>
                          <span
                            className={`px-2 py-1 inline-flex text-xs leading-5 font-medium rounded-full source-sans-3 ${
                              dept.status === "processed"
                                ? "bg-green-100 text-green-800"
                                : "bg-amber-100 text-amber-800"
                            }`}
                          >
                            {dept.status.charAt(0).toUpperCase() +
                              dept.status.slice(1)}
                          </span>
                        </div>
                        <div className="space-y-1">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-500 source-sans-3">
                              Employees:
                            </span>
                            <span className="text-sm font-medium text-gray-700 source-sans-3">
                              {dept.employees}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-500 source-sans-3">
                              Gross Amount:
                            </span>
                            <span className="text-sm font-medium text-gray-700 source-sans-3">
                              ${dept.grossAmount.toLocaleString()}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-500 source-sans-3">
                              Net Amount:
                            </span>
                            <span className="text-sm font-medium text-gray-700 source-sans-3">
                              ${dept.netAmount.toLocaleString()}
                            </span>
                          </div>
                        </div>
                        <div className="mt-4 pt-3 border-t border-gray-100 flex justify-end">
                          <button className="text-sm text-primary-600 hover:text-primary-800 source-sans-3">
                            View Details
                          </button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  <div className="border border-gray-200 rounded-lg p-5">
                    <h4 className="font-medium text-gray-900 source-sans-3 mb-4">
                      Department Comparison
                    </h4>
                    <div className="space-y-4">
                    {departmentsPayroll.map((dept) => (
                        <div key={dept.id}>
                            <div className="flex justify-between mb-1">
                            <span className="text-sm font-medium text-gray-700 source-sans-3">{dept.name}</span>
                            <span className="text-sm font-medium text-gray-700 source-sans-3">
                                ${dept.grossAmount.toLocaleString()}
                            </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                                className="bg-primary-600 h-2 rounded-full"
                                style={{
                                width: `${
                                    payrollSummary.totalGrossPay > 0
                                    ? (dept.grossAmount / payrollSummary.totalGrossPay) * 100
                                    : 0
                                }%`,
                                }}
                            ></div>
                            </div>
                        </div>
                    ))}
                      <div>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-medium text-gray-700 source-sans-3">
                            Marketing
                          </span>
                          <span className="text-sm font-medium text-gray-700 source-sans-3">
                            $
                            {departmentsPayroll.find(
                              (d) => d.name === "Marketing"
                            )?.grossAmount.toLocaleString()}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-emerald-500 h-2 rounded-full"
                            style={{
                              width: `${
                                (departmentsPayroll.find(
                                  (d) => d.name === "Marketing"
                                )?.grossAmount /
                                  payrollSummary.totalGrossPay) *
                                  100 || 0
                              }%`,
                            }}
                          ></div>
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-medium text-gray-700 source-sans-3">
                            Operations
                          </span>
                          <span className="text-sm font-medium text-gray-700 source-sans-3">
                            $
                            {departmentsPayroll.find(
                              (d) => d.name === "Operations"
                            )?.grossAmount.toLocaleString()}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-amber-500 h-2 rounded-full"
                            style={{
                              width: `${
                                (departmentsPayroll.find(
                                  (d) => d.name === "Operations"
                                )?.grossAmount /
                                  payrollSummary.totalGrossPay) *
                                  100 || 0
                              }%`,
                            }}
                          ></div>
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-medium text-gray-700 source-sans-3">
                            Finance
                          </span>
                          <span className="text-sm font-medium text-gray-700 source-sans-3">
                            $
                            {departmentsPayroll.find(
                              (d) => d.name === "Finance"
                            )?.grossAmount.toLocaleString()}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-rose-500 h-2 rounded-full"
                            style={{
                              width: `${
                                (departmentsPayroll.find(
                                  (d) => d.name === "Finance"
                                )?.grossAmount /
                                  payrollSummary.totalGrossPay) *
                                  100 || 0
                              }%`,
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <motion.div
              variants={cardVariants}
              custom={0}
              initial="hidden"
              animate="visible"
              className="bg-white rounded-xl shadow-sm overflow-hidden"
            >
              <div className="p-5 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-lg text-primary-800 source-sans-3">
                    Payroll Processing History
                  </h3>
                  <div className="flex space-x-2">
                    <select className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg source-sans-3 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500">
                      <option value="">Last 6 Months</option>
                      <option value="">Last 12 Months</option>
                      <option value="">All Time</option>
                    </select>
                    <button className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg source-sans-3 hover:bg-gray-50 flex items-center">
                      <svg
                        className="w-4 h-4 mr-1"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                        />
                      </svg>
                      Export
                    </button>
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider source-sans-3">
                        Period
                      </th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider source-sans-3">
                        Total Amount
                      </th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider source-sans-3">
                        Processed On
                      </th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider source-sans-3">
                        Processed By
                      </th>
                      <th className="py-3 px-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider source-sans-3">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {payrollHistory.map((history) => (
                      <tr key={history.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 source-sans-3">
                            {history.period}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 source-sans-3">
                            ${history.totalAmount.toLocaleString()}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 source-sans-3">
                            {history.processedOn}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 source-sans-3">
                            {history.processedBy}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button className="text-primary-600 hover:text-primary-800 source-sans-3 mr-3">
                            Download
                          </button>
                          <button className="text-gray-500 hover:text-gray-700 source-sans-3">
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-5 py-4 border-t border-gray-200">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                  <div className="text-sm text-gray-500 source-sans-3 mb-4 md:mb-0">
                    Showing the last {payrollHistory.length} processed payrolls
                  </div>
                  <div className="flex justify-end">
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                        <span className="sr-only">Previous</span>
                        <svg
                          className="h-5 w-5"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 19l-7-7 7-7"
                          />
                        </svg>
                      </button>
                      <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-primary-50 text-sm font-medium text-primary-700">
                        1
                      </button>
                      <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50">
                        2
                      </button>
                      <button className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                        <span className="sr-only">Next</span>
                        <svg
                          className="h-5 w-5"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Filter Modal */}
          {showFilterModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white rounded-lg max-w-md w-full overflow-hidden"
              >
                <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="font-bold text-lg text-primary-800 source-sans-3">
                    Filter Employees
                  </h3>
                  <button
                    onClick={() => setShowFilterModal(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <svg
                      className="h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <div className="p-4">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 source-sans-3 mb-1">
                        Department
                      </label>
                      <select className="w-full px-3 py-2 border border-gray-300 rounded-lg source-sans-3 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500">
                        <option value="">All Departments</option>
                        <option value="Engineering">Engineering</option>
                        <option value="Marketing">Marketing</option>
                        <option value="Operations">Operations</option>
                        <option value="Finance">Finance</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 source-sans-3 mb-1">
                        Status
                      </label>
                      <select className="w-full px-3 py-2 border border-gray-300 rounded-lg source-sans-3 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500">
                        <option value="">All Status</option>
                        <option value="processed">Processed</option>
                        <option value="pending">Pending</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 source-sans-3 mb-1">
                        Salary Range
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          placeholder="Min"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg source-sans-3 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                        />
                        <span className="text-gray-500">-</span>
                        <input
                          type="number"
                          placeholder="Max"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg source-sans-3 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      onClick={() => setShowFilterModal(false)}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 source-sans-3 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => setShowFilterModal(false)}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg source-sans-3 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Payroll;
